"use client";

import type { PlannerPreferences } from "@/lib/planner/preferences";
import type { AreaSport } from "@/lib/planner/graph-types";
import {
  applyDayStyle,
  dayStylesForSport,
  isValidDayStyle,
  type DayStyle,
} from "@/lib/planner/day-styles";

type Props = {
  prefs: PlannerPreferences;
  sport: AreaSport;
  touchedFields: readonly string[];
  activeStyle: DayStyle | null;
  onChange: (
    next: PlannerPreferences,
    touchedPath?: string,
    selectedStyle?: DayStyle,
  ) => void;
  onOpenFineTune?: () => void;
};

// Friendly context inputs -> heavy preference fields (design doc section 8).
const WHO: { value: PlannerPreferences["group"]; label: string }[] = [
  { value: "solo", label: "Solo" },
  { value: "couple", label: "Partner" },
  { value: "mates", label: "Mates" },
  { value: "family", label: "Family" },
];
const LEVEL: { value: PlannerPreferences["ability"]; label: string }[] = [
  { value: "beginner", label: "New" },
  { value: "intermediate", label: "Cruiser" },
  { value: "advanced", label: "Strong" },
  { value: "expert", label: "Charger" },
];
const HOW_LONG: { value: PlannerPreferences["distance"]; label: string }[] = [
  { value: "short", label: "Few hours" },
  { value: "half-day", label: "Half day" },
  { value: "full-day", label: "Full day" },
  { value: "max", label: "Max km" },
];

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[10px] border border-border-light bg-white px-2.5 text-[12.5px] font-semibold text-navy";

export const CUSTOM_STYLE_VALUE = "";

export function tweakBarStyleValue(activeStyle: DayStyle | null): string {
  return activeStyle ?? CUSTOM_STYLE_VALUE;
}

export function TweakBar({
  prefs,
  sport,
  touchedFields,
  activeStyle,
  onChange,
  onOpenFineTune,
}: Props) {
  const styles = dayStylesForSport(sport);
  const set = <K extends keyof PlannerPreferences>(key: K, value: PlannerPreferences[K]) =>
    onChange({ ...prefs, [key]: value } as PlannerPreferences, String(key));

  return (
    <div className="sticky top-2 z-10 mb-4 rounded-[14px] border border-border-light bg-white/95 p-3 shadow-sm backdrop-blur">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">Who</span>
          <select aria-label="Who is out" className={SELECT_CLASS} value={prefs.group} onChange={(e) => set("group", e.target.value as PlannerPreferences["group"])}>
            {WHO.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">Level</span>
          <select aria-label="Your level" className={SELECT_CLASS} value={prefs.ability} onChange={(e) => set("ability", e.target.value as PlannerPreferences["ability"])}>
            {LEVEL.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">How long</span>
          <select aria-label="How long" className={SELECT_CLASS} value={prefs.distance} onChange={(e) => set("distance", e.target.value as PlannerPreferences["distance"])}>
            {HOW_LONG.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.5px] text-text-muted">Style</span>
          <select
            aria-label="Day style"
            className={SELECT_CLASS}
            value={tweakBarStyleValue(activeStyle)}
            onChange={(e) => {
              const key = e.target.value;
              if (!isValidDayStyle(sport, key)) return;
              onChange(applyDayStyle(prefs, key, touchedFields), "dayStyle", key);
            }}
          >
            {activeStyle === null ? <option value={CUSTOM_STYLE_VALUE}>Custom</option> : null}
            {styles.map((style) => <option key={style.key} value={style.key}>{style.label}</option>)}
          </select>
        </label>
      </div>
      {onOpenFineTune && <button
        type="button"
        onClick={onOpenFineTune}
        className="mt-2 inline-flex min-h-[44px] items-center text-[12.5px] font-semibold text-gold no-underline hover:underline"
      >
        Fine-tune everything
      </button>}
    </div>
  );
}
